import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, Loader2, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase, dbService, emailConfirmationService } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getPendingVerificationEmail, clearPendingVerificationEmail } from '../utils/sessionStorage';
import { getSafeRedirectFromLocationState } from '../utils/safeRedirect';
import toast from 'react-hot-toast';

const EmailVerificationPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signOut, loading } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [userProfileExists, setUserProfileExists] = useState<boolean | null>(null);
  const [isAlreadyVerified, setIsAlreadyVerified] = useState(false);
  const [isProfileChecking, setIsProfileChecking] = useState(false);
  
  // Check if user came from login attempt or signup
  const fromLogin = searchParams.get('from') === 'login';
  const fromSignup = searchParams.get('from') === 'signup';
  
  // Get email from session storage instead of URL parameter
  const emailFromStorage = getPendingVerificationEmail();
  
  // Use email from user object or session storage
  const userEmail = user?.email || emailFromStorage;

  // Check if user is already verified and redirect them
  useEffect(() => {
    if (!loading && user?.email_confirmed_at) {
      setIsAlreadyVerified(true);
      
      // Clean up session storage
      clearPendingVerificationEmail();
      
      // Auto-redirect after showing the message
      setTimeout(() => {
        const redirectToRolePage = async () => {
          try {
            const { data: userProfile } = await supabase
              .from('users')
              .select('role')
              .eq('id', user.id)
              .single();

            let redirectPath = '/matchmaking';

            if (userProfile?.role === 'cdmo') {
              // Check if CDMO profile is complete
              const profileStatus = await dbService.checkCDMOProfileCompletion(user.id || '');

              redirectPath = (!profileStatus.hasProfile || !profileStatus.isComplete)
                ? '/cdmo-application'
                : '/cdmo-dashboard';
            }

            // If the user was bounced here from a specific protected page (e.g. an
            // email "Go to Dashboard" link), return them there instead of the
            // role-based default (client-reported issue #5-2).
            const requestedPath = getSafeRedirectFromLocationState(location.state);
            navigate(requestedPath || redirectPath);
          } catch (error) {
            console.error('Error fetching user profile:', error);
            navigate('/matchmaking');
          }
        };

        redirectToRolePage();
      }, 3000); // Give user 3 seconds to see the message
    }
  }, [loading, user, navigate]);

  // Check if user profile exists in custom users table when coming from login
  useEffect(() => {
    const checkUserProfile = async () => {
      // Only check profile if coming from login and not already verified
      if (fromLogin && !isAlreadyVerified && !loading) {
        setIsProfileChecking(true);
        try {
          if (user?.id) {
            // If we have user ID, check by ID
            const profile = await dbService.getUserProfile(user.id);
            setUserProfileExists(!!profile);
          } else if (emailFromStorage) {
            // If we don't have user ID but have email, check by email
            const { data, error } = await supabase
              .from('users')
              .select('id')
              .eq('email', emailFromStorage)
              .single();
            
            if (error && error.code !== 'PGRST116') {
              console.error('Error checking user profile by email:', error);
              setUserProfileExists(false);
            } else {
              setUserProfileExists(!!data);
            }
          } else {
            // No user ID or email available - session expired
            setUserProfileExists(false);
          }
        } catch (error) {
          console.error('Error checking user profile:', error);
          setUserProfileExists(false);
        } finally {
          setIsProfileChecking(false);
        }
      } else if (!fromLogin) {
        // Not from login, so we don't need to check profile
        setIsProfileChecking(false);
      }
    };

    checkUserProfile();
  }, [fromLogin, user?.id, emailFromStorage, isAlreadyVerified, loading]);

  const handleResendConfirmation = async () => {
    if (!userEmail) {
      toast.error('No email address found. Please sign up again.');
      navigate('/startup-signup');
      return;
    }

    setIsResending(true);
    try {
      await emailConfirmationService.resendConfirmationEmail(userEmail);
      toast.success('Confirmation email sent! Please check your inbox and spam folder.');
    } catch (error: unknown) {
      console.error('Error resending confirmation:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('rate limit')) {
        toast.error('Please wait before requesting another confirmation email.');
      } else {
        toast.error('Failed to resend confirmation email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Clean up session storage when component unmounts or user verifies
  useEffect(() => {
    return () => {
      if (user?.email_confirmed_at) {
        clearPendingVerificationEmail();
      }
    };
  }, [user?.email_confirmed_at]);

  // Show loading state while auth is loading or profile is being checked
  if (loading || (fromLogin && isProfileChecking)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 w-full max-w-md border border-white/50">
          <div className="flex items-center justify-center mb-8">
            <Mail className="w-8 h-8 text-electric-blue mr-3" />
            <span className="text-xl font-bold text-charcoal">Pharma Connect Navigator</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-charcoal mb-2">
              {loading ? 'Checking Authentication' : 'Checking Account Status'}
            </h2>
            <p className="text-slate-gray">Please wait...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show already verified message
  if (isAlreadyVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 w-full max-w-md border border-white/50">
          <div className="flex items-center justify-center mb-8">
            <Mail className="w-8 h-8 text-electric-blue mr-3" />
            <span className="text-xl font-bold text-charcoal">Pharma Connect Navigator</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-charcoal mb-4">Email Already Verified</h1>
            <p className="text-slate-gray mb-6">
              Your email address has already been verified. You'll be redirected to your dashboard shortly.
            </p>
            <div className="flex items-center justify-center text-sm text-slate-gray">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Redirecting...
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 w-full max-w-md border border-white/50">
        <div className="flex items-center justify-center mb-8">
          <Mail className="w-8 h-8 text-electric-blue mr-3" />
          <span className="text-xl font-bold text-charcoal">Pharma Connect Navigator</span>
        </div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-electric-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-electric-blue" />
          </div>
          
          <h1 className="text-2xl font-bold text-charcoal mb-4">
            {fromLogin ? 'Email Verification Required' : fromSignup ? 'Account Created Successfully!' : 'Check Your Email'}
          </h1>
          
          {fromLogin && userProfileExists === false && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 border rounded-lg bg-red-50 border-red-200"
            >
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 text-red-600" />
                <div>
                  <p className="text-red-800 text-sm font-medium mb-1">
                    Session Expired
                  </p>
                  <p className="text-red-700 text-sm">
                    Your account exists but your session is expired. 
                    Please sign in again.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          
          {fromLogin && userProfileExists === true && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 border rounded-lg bg-amber-50 border-amber-200"
            >
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 text-amber-600" />
                <div>
                  <p className="text-amber-800 text-sm font-medium mb-1">
                    Account Found but Not Verified
                  </p>
                  <p className="text-amber-700 text-sm">
                    We found your account, but your email address hasn't been verified yet. 
                    Please verify your email to access your account.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          
          {fromSignup ? (
            <>
              <p className="text-slate-gray mb-6 text-base">
                We have sent a verification email to your registered email address. Please verify to continue.
              </p>
              <p className="text-charcoal font-medium mb-6 bg-sky-blue/10 p-3 rounded-lg">
                {userEmail}
              </p>
              <p className="text-slate-gray mb-8 text-sm">
                Click the link in the email to verify your account and complete your registration. Please check your inbox and spam folder.
              </p>
            </>
          ) : (
            <>
              <p className="text-slate-gray mb-2">
                {fromLogin ? 'Please verify your email address:' : 'We\'ve sent a confirmation link to:'}
              </p>
              <p className="text-charcoal font-medium mb-6">
                {userEmail}
              </p>
              
              <p className="text-slate-gray mb-8 text-sm">
                {fromLogin 
                  ? 'Click the link in the email to verify your account and gain access to your dashboard. Don\'t forget to check your spam folder!'
                  : 'Click the link in the email to verify your account and complete your registration. Don\'t forget to check your spam folder!'
                }
              </p>
            </>
          )}
          
          <div className="space-y-4">
            {fromSignup ? (
              // For fresh signups, only show navigation options, no resend button
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 text-slate-gray hover:text-charcoal transition-colors font-medium flex items-center justify-center py-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </button>
                
                <button
                  onClick={handleSignOut}
                  className="flex-1 text-slate-gray hover:text-charcoal transition-colors font-medium py-2"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              // For login attempts or other cases, show resend options
              <>
                {fromLogin && userProfileExists === false ? (
                  <button
                    onClick={() => navigate('/startup-signup')}
                    className="w-full bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center"
                  >
                    Sign In Again
                  </button>
                ) : (
                  <button
                    onClick={handleResendConfirmation}
                    disabled={isResending}
                    className="w-full bg-electric-blue text-white px-6 py-3 rounded-lg font-medium hover:bg-electric-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Resend Confirmation Email
                      </>
                    )}
                  </button>
                )}
                
                <div className="flex space-x-4">
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 text-slate-gray hover:text-charcoal transition-colors font-medium flex items-center justify-center py-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                  </button>
                  
                  <button
                    onClick={handleSignOut}
                    className="flex-1 text-slate-gray hover:text-charcoal transition-colors font-medium py-2"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
          
          <div className="mt-8 p-4 bg-sky-blue/10 rounded-lg">
            <p className="text-sm text-slate-gray">
              <strong>Didn't receive the email?</strong><br />
              • <strong className="text-charcoal">Check your spam/junk folder</strong><br />
              • Make sure {userEmail} is correct<br />
              {fromSignup ? (
                '• You can request a resend later if needed'
              ) : (
                '• Try resending the confirmation email'
              )}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EmailVerificationPendingPage;
